import { useState } from 'react'
import { Link, useLocation, useNavigate, Outlet } from 'react-router-dom'
import { LogOut } from 'lucide-react'
import { Sheet, SheetContent } from '@/components/ui/sheet'
import { Button, buttonVariants } from '@/components/ui/button'
import { Avatar, AvatarFallback } from '@/components/ui/avatar'
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from '@/components/ui/dropdown-menu'
import { useAuth } from '@/contexts/AuthContext'
import { useBrand } from '@/brand/BrandProvider'
import { AppFooter } from '@/components/AppFooter'
import { FirmProfileGate } from '@/components/FirmProfileGate'
import { useBrandRegistrationStatus } from '@/hooks/useBrandRegistrationStatus'
import { navItemsFor, type NavItem } from '@/lib/productNav'
import { userInitials, AVATAR_BG, AVATAR_FG } from '@/lib/userInitials'
import { cn } from '@/lib/utils'

/**
 * Roles the router lets through to `/firm-profile` (see `router/index.tsx`).
 * Mirrored here so the account menu never offers a row that `ProtectedRoute`
 * would silently bounce off its landing redirect — a dead click reads as a
 * broken menu, which is the exact bug this menu was added to fix.
 */
export const FIRM_PROFILE_ROLES = ['super_admin', 'cpa', 'firm', 'tester']

/**
 * ACCOUNT-MENU-1: who gets the "Firm Profile" row in the ACCOUNT menu.
 *
 * Narrower than FIRM_PROFILE_ROLES by exactly one role. `super_admin` is
 * platform-level, not a member of one firm, so offering one firm's legal
 * identity page from the menu that sits under *their own name* misstates whose
 * account it is. They lose nothing: the route still admits them and the sidebar
 * keeps ADMIN → Firm Profile (see productNav.ts), where it reads as the admin
 * surface it actually is.
 */
export const ACCOUNT_MENU_FIRM_PROFILE_ROLES = FIRM_PROFILE_ROLES.filter(r => r !== 'super_admin')

/**
 * Friendly names for the role token in the menu header. `super_admin` is an
 * internal enum value; a CPA reading their own menu should not have to.
 *
 * Exported for /account (USER-PROFILE-1), which shows the same role to the same
 * person one click later — two spellings of one role on two surfaces would read
 * as two different facts.
 */
export const ROLE_LABELS: Record<string, string> = {
  super_admin: 'Platform admin',
  cpa: 'CPA',
  firm: 'Firm',
  tester: 'Tester',
  analyst: 'Analyst',
  valuation_signer: 'Valuation signer',
  valuation_owner: 'Valuation owner',
  pending_approval: 'Pending approval',
}

export function AppShell() {
  const { user, logout } = useAuth()
  const { theme } = useBrand()
  const location = useLocation()
  const navigate = useNavigate()
  const [mobileOpen, setMobileOpen] = useState(false)
  const { hasApprovedBrand } = useBrandRegistrationStatus()
  const showsFirmProfileRow = !!user?.role && ACCOUNT_MENU_FIRM_PROFILE_ROLES.includes(user.role)
  // super_admin's home firm is a real row in `users`, but printing it here
  // beside their name reads as "this is your firm" — the same misstatement the
  // duplicated profile rows made. Show the role alone for them.
  const accountIdentity = [
    user?.role ? (ROLE_LABELS[user.role] ?? user.role) : '',
    user?.role === 'super_admin' ? '' : user?.firm,
  ]
    .filter(Boolean)
    .join(' · ')

  // RBAC-1: resolved against the account's entitlements, which mark items
  // rather than removing them. The only thing filtered out here is the
  // brand-registration link once a brand is approved — that is a completed
  // task disappearing, not an entitlement, and it stays a filter.
  const allNavItems = navItemsFor(user?.role, user?.products)
  const navItems = allNavItems.filter(item => {
    if (item.href === '/brand-registration' && hasApprovedBrand) return false
    return true
  })
  const EXTRA_ROUTE_TITLES: Array<{ test: (path: string) => boolean; label: string }> = [
    { test: p => p === '/getting-started', label: 'Getting Started' },
    { test: p => p === '/brand-registration', label: 'Register a Brand' },
    { test: p => p === '/firm-profile', label: 'Firm Profile' },
    { test: p => p === '/account', label: 'My Account' },
    { test: p => p.startsWith('/qoe/financial-statements/') && p.endsWith('/review'), label: 'Financial Statement Review' },
    { test: p => /^\/deals\/[^/]+\/entities$/.test(p), label: 'Entity Structure' },
    { test: p => /^\/deals\/[^/]+\/tax$/.test(p), label: 'Tax Document Review' },
    { test: p => /^\/deals\/[^/]+$/.test(p), label: 'Deal Detail' },
  ]
  const currentPageTitle =
    navItems.find(item => item.href === location.pathname)?.label ??
    EXTRA_ROUTE_TITLES.find(r => r.test(location.pathname))?.label ??
    'Dashboard'

  const NAV_SECTIONS: Array<{ key: string; label: string }> = [
    { key: 'workspace', label: 'WORKSPACE' },
    { key: 'documents', label: 'DOCUMENTS' },
    { key: 'admin', label: 'ADMIN' },
  ]

  function SidebarContent() {
    const initials = userInitials(user?.name)
    const grouped = NAV_SECTIONS.map(s => ({
      ...s,
      items: navItems.filter(item => item.section === s.key),
    })).filter(s => s.items.length > 0)
    const ungrouped = navItems.filter(item => !item.section)

    const linkStyle = (active: boolean) => active ? {
      display: 'flex', alignItems: 'center', gap: '10px',
      padding: '7px 12px', borderRadius: '6px', fontSize: '13px',
      fontWeight: 600, color: 'var(--sidebar-accent-foreground)',
      backgroundColor: 'var(--sidebar-accent)',
      borderLeft: '3px solid var(--sidebar-primary)',
      textDecoration: 'none', marginBottom: '2px',
    } : {
      display: 'flex', alignItems: 'center', gap: '10px',
      padding: '7px 12px 7px 15px', borderRadius: '6px', fontSize: '13px',
      fontWeight: 500, color: 'var(--sidebar-muted)',
      textDecoration: 'none', marginBottom: '2px',
    }

    /**
     * One nav row. An item the account cannot open renders as a greyed,
     * non-clickable span — VISIBLE, never hidden (RBAC-1 build item 4). A firm
     * that cannot see a product exists will never ask to buy it, so hiding is
     * the one thing this must not do. The reason is on the row, and it
     * distinguishes "your firm has not bought this" from "your firm has it,
     * ask your partner" — different problems, different next steps.
     *
     * This is presentation only. The server refuses the route regardless.
     */
    function NavEntry({ item }: { item: NavItem }) {
      if (item.accessible === false) {
        return (
          <span
            key={item.href}
            aria-disabled="true"
            data-entitled={item.entitled ? 'true' : 'false'}
            title={item.disabledReason}
            style={{
              ...linkStyle(false),
              color: 'rgba(255,255,255,0.32)',
              cursor: 'not-allowed',
            }}
          >
            <span style={{ fontSize: '15px', lineHeight: 1 }}>{item.icon}</span>
            {item.label}
            <span style={{ marginLeft: 'auto', fontSize: '10px', letterSpacing: '0.04em' }}>
              {item.entitled ? 'NO ACCESS' : 'LOCKED'}
            </span>
          </span>
        )
      }
      return (
        <Link
          to={item.href}
          onClick={() => setMobileOpen(false)}
          style={linkStyle(location.pathname === item.href)}
        >
          <span style={{ fontSize: '15px', lineHeight: 1 }}>{item.icon}</span>
          {item.label}
        </Link>
      )
    }

    return (
      <nav className="flex flex-col h-full" style={{ backgroundColor: 'var(--sidebar)' }}>
        {/* Brand mark — gold "Y" (same mark as /login; brand logo endpoint 404s) */}
        <div className="px-5 pt-5 pb-3">
          <div
            aria-label={theme.name}
            style={{
              width: '28px', height: '28px', borderRadius: '6px',
              backgroundColor: 'var(--sidebar-primary)',
              display: 'flex', alignItems: 'center', justifyContent: 'center',
            }}
          >
            <span style={{ color: 'var(--sidebar-primary-foreground)', fontWeight: 800, fontSize: '15px', fontFamily: 'Georgia, serif', lineHeight: 1 }}>Y</span>
          </div>
        </div>
        <div style={{ height: '2px', backgroundColor: 'var(--sidebar-primary)', margin: '0 20px 16px' }} />

        {/* Nav groups */}
        <div className="flex-1 px-3 overflow-y-auto">
          {grouped.map(section => (
            <div key={section.key} className="mb-4">
              <p style={{ fontSize: '10px', fontWeight: 700, letterSpacing: '0.1em', color: 'var(--sidebar-muted)', padding: '0 8px 6px', margin: 0 }}>
                {section.label}
              </p>
              {section.items.map(item => (
                <NavEntry key={item.href} item={item} />
              ))}
            </div>
          ))}
          {ungrouped.map(item => (
            <NavEntry key={item.href} item={item} />
          ))}
        </div>

        {/* User footer */}
        <div style={{ borderTop: '1px solid var(--sidebar-border)', padding: '14px 16px' }}>
          <div className="flex items-center gap-2 mb-3">
            <div data-testid="sidebar-avatar" style={{
              width: 28, height: 28, borderRadius: '50%',
              backgroundColor: AVATAR_BG, display: 'flex',
              alignItems: 'center', justifyContent: 'center',
              fontSize: '11px', fontWeight: 700, color: AVATAR_FG, flexShrink: 0,
            }}>
              {initials}
            </div>
            <div className="min-w-0 flex-1">
              <p style={{ fontSize: '12px', fontWeight: 600, color: 'var(--sidebar-foreground)', margin: 0, lineHeight: 1.3 }} className="truncate">
                {user?.name}
              </p>
              <p style={{ fontSize: '10px', color: 'var(--sidebar-muted)', margin: 0 }} className="truncate">
                {user?.firm}
              </p>
            </div>
          </div>
          <Button
            variant="ghost"
            size="sm"
            onClick={logout}
            className="w-full text-gray-300 hover:text-white hover:bg-white/10 justify-start"
          >
            <LogOut className="h-4 w-4 mr-2" />
            Sign out
          </Button>
        </div>
      </nav>
    )
  }

  return (
    <div className="flex h-screen overflow-hidden" style={{ backgroundColor: 'var(--background)' }}>
      {/* Desktop sidebar */}
      <aside className="hidden md:flex w-64 flex-shrink-0 flex-col" style={{ backgroundColor: 'var(--sidebar)' }}>
        <SidebarContent />
      </aside>

      {/* Mobile sidebar (Sheet — controlled mode) */}
      <Sheet open={mobileOpen} onOpenChange={setMobileOpen}>
        <SheetContent side="left" className="w-64 p-0" showCloseButton={false}>
          <SidebarContent />
        </SheetContent>
      </Sheet>

      {/* Main area */}
      <div className="flex-1 flex flex-col min-w-0 overflow-hidden" style={{ backgroundColor: 'var(--background)' }}>
        {/* Top header */}
        <header className="h-14 border-b border-gray-200 flex items-center justify-between px-4 flex-shrink-0" style={{ backgroundColor: 'var(--card)' }}>
          {/* Mobile menu button */}
          <Button
            variant="ghost"
            size="icon"
            className="md:hidden"
            onClick={() => setMobileOpen(true)}
          >
            ☰
          </Button>
          <div className="hidden md:flex items-center gap-1.5" style={{ fontSize: '12px', color: 'var(--pal-ink-soft)' }}>
            <span>Home</span>
            <span style={{ color: '#ccc' }}>&rsaquo;</span>
            <span style={{ color: 'var(--primary)', fontWeight: 500 }}>{currentPageTitle}</span>
          </div>

          {/* Account menu.
              The trigger renders base-ui's own <button> and borrows the ghost
              button's classes. It used to be `render={<Button/>}`, which handed
              MenuTrigger's ref to a plain function component — React 18 drops
              that ref ("Function components cannot be given refs"), so the
              trigger had no anchor and clicking the name opened nothing. That
              is the bug behind "the top-right is a non-functional label". */}
          <DropdownMenu>
            <DropdownMenuTrigger
              data-testid="account-menu-trigger"
              aria-label="Account menu"
              className={cn(buttonVariants({ variant: 'ghost' }), 'flex items-center gap-2')}
            >
              <Avatar className="h-7 w-7">
                <AvatarFallback
                  data-testid="header-avatar"
                  className="text-xs font-bold"
                  style={{ backgroundColor: AVATAR_BG, color: AVATAR_FG }}
                >
                  {userInitials(user?.name)}
                </AvatarFallback>
              </Avatar>
              <span className="hidden sm:block text-sm font-medium">{user?.name}</span>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end">
              <DropdownMenuItem disabled className="text-xs text-gray-500">
                {accountIdentity}
              </DropdownMenuItem>
              {/* USER-PROFILE-1: "My Account" is back, and now it opens a real
                  personal page (/account) rather than a second label for
                  /firm-profile — the duplication ACCOUNT-MENU-1 removed.
                  Ungated on purpose: it is the signed-in person's OWN account,
                  so every authenticated role has one, super_admin included.
                  That is what makes it the row that is always correct, unlike
                  "Firm Profile" below, which asserts membership in one firm. */}
              <DropdownMenuItem onClick={() => navigate('/account')}>
                My Account
              </DropdownMenuItem>
              {/* One row, named for what it opens: /firm-profile is the FIRM's
                  legal identity (legal name, licence number, CPA of record),
                  which is a different thing from the person reading the menu —
                  hence two rows, two routes, two honest labels. */}
              {showsFirmProfileRow && (
                <DropdownMenuItem onClick={() => navigate('/firm-profile')}>
                  Firm Profile
                </DropdownMenuItem>
              )}
              <DropdownMenuItem onClick={logout} className="text-red-600">
                Sign out
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        </header>

        {/* Page content */}
        <main className="flex-1 overflow-auto p-4 md:p-6">
          <FirmProfileGate />
          <Outlet />
        </main>

        <AppFooter />
      </div>
    </div>
  )
}
